import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {ColumnConfig, Reports} from '../../config/reportTypes';

import clsx from 'clsx';
import {t} from 'i18next';
import {ToastMessageConfigType} from '../../../../../api/types/ingestionApi.type';
import {ReviewerProductivityViewPayload} from '../../../../../api/types/reportingApi.type';
import {
  useGetReportState,
  useExportReviewerCounts,
  useLeftPanel,
  useLeftPanelReport,
  useReviewerProductivityReport,
  useReviewerProductivityView,
} from '../../../../../hooks/reporting/useReporting';
import {ReportTypeCode, ToastType} from '../../../../../shared/constants';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';

import {sortBy} from 'lodash';
import {isEmpty} from '../../../../../utils/formatters';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {ReportGridProps} from '../../component/right-panel/ReportDataGrid';
import ArrWaitToCompleteModalWrapper from '../../component/waittocomplete-modal-wrapper/ArrWaitToCompleteModalWrapper';
import {StandardReports} from '../../StandardReports';
import {
  getSelectedReviewer,
  mergeApiDataIntoConfig,
  reviewerProductivityColumnConfig,
  useReviewerProductivityLocationParams,
} from './Helper';
import './ReviewerProductivityContainer.scss';

dayjs.extend(utc);

const REPORT_CONFIG_FILE_NAME = 'ReviewerProductivityReport';
const REPORT_TYPE_CODE = 6;
const REPORT_ACTION_TYPE = 'EXPORTREPORT';
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const INIT_PAGE_SIZE = 10;
const INIT_PAGE_NUMBER = 1;
const ROW_PRIMARY_KEY = 'id';

const ReviewerProductivityContainer: React.FC = () => {
  const {urlReportId, reportLabel, isBackgroundReport} =
    useReviewerProductivityLocationParams();

  const {data: leftPanelReport} = useLeftPanelReport(urlReportId);
  const {data: leftPanelDetails} = useLeftPanel(REPORT_TYPE_CODE);

  const {
    isLoading: isReportStateLoading,
    data: savedReportStateData,
    error: reportStateError,
  } = useGetReportState(urlReportId, isBackgroundReport);

  const {
    mutate: fetchReviewerProductivityReport,
    isPending: isProductivityReportLoading,
    reset: resetReviewerProductivityReport,
  } = useReviewerProductivityReport();

  const {
    mutate: fetchReviewerProductivityView,
    isPending: isProductivityReviewLoading,
    reset: resetReviewerProductivityView,
  } = useReviewerProductivityView();

  const {
    mutate: exportReviewerCounts,
    isPending: isExportReviewerCountsLoading,
  } = useExportReviewerCounts();

  const defaultReportConfig = useRef<any>(null);
  const hiddenColumnsRef = useRef<number[]>([]);
  const resetLeftPanelReport = useRef<boolean>(false);

  const [savedFilterValues, setSavedFilterValues] = useState<
    Record<string, any>
  >({});

  const [reportConfig, setReportConfig] = useState<Reports>();

  const [productivityViewPayload, setProductivityViewPayload] = useState<{
    reportId: number;
    notificationId?: number;
    reportFilePath: string;
    reportName: string;
    assignmentFlag: boolean;
  }>();

  const [reportGridData, setReportGridData] = useState<Record<string, any>[]>(
    []
  );

  const [pageSize, setPageSize] = useState<number>(INIT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState<number>(INIT_PAGE_NUMBER);
  const [columnConfig, setColumnConfig] = useState<ColumnConfig[]>([
    ...reviewerProductivityColumnConfig,
  ]);
  const [isQueryRun, setIsQueryRun] = useState(false);

  const [toastMsgConfig, setToastMsgConfig] = useState<
    ToastMessageConfigType & {show: boolean}
  >({
    show: false,
  });

  const [dataAsOf, setDataAsOf] = useState<string>(() =>
    dayjs.utc().format('YYYY/MM/DD HH:mm:ss')
  );

  const setColumnVisibility = useCallback(() => {
    setColumnConfig(
      (reviewerProductivityColumnConfig ?? []).map(
        (column: ColumnConfig, index: number) => ({
          ...column,
          isVisible: !hiddenColumnsRef.current.includes(index),
        })
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(hiddenColumnsRef.current)]);

  useEffect(() => {
    setColumnVisibility();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setColumnVisibility, JSON.stringify(hiddenColumnsRef.current)]);

  const reviewerProductivityReportRequest = useCallback(
    (_filterValues: Record<string, any>) => {
      const {
        selectedReviewers = [],
        selectedAssignments = [],
        fromDateTime,
        toDateTime,
      } = _filterValues;
      fetchReviewerProductivityReport(
        {
          selectedReviewers,
          selectedAssignments,
          fromDateTime,
          toDateTime,
          selectedCustomColumns: [],
          reportTypeCode: ReportTypeCode.REVIEWER_PRODUCTIVITY_REPORT,
          assignmentFlag: selectedAssignments.length > 0,
        },
        {
          onSuccess: (data) => {
            const {
              reportID: reportId,
              notificationID: notificationId,
              reportName,
              reportFilePath,
            } = data;

            setProductivityViewPayload({
              reportId,
              notificationId,
              reportName,
              reportFilePath,
              assignmentFlag: selectedAssignments.length > 0,
            });
          },
          onError: (error) => {
            setToastMsgConfig({
              show: true,
              type: ToastType.Error,
              message:
                error?.message ??
                t('reports.reviewerProductivity.reportAPIError'),
            });
          },
        }
      );
    },
    [fetchReviewerProductivityReport]
  );

  const reviewerProductivityViewRequest = useCallback(
    (payload: ReviewerProductivityViewPayload) => {
      fetchReviewerProductivityView(payload, {
        onSuccess: (data) => {
          const {reviewerProductivityReportDetails = []} = data;
          const updatedReportGridData = reviewerProductivityReportDetails.map(
            (item: Record<string, any>, index: number) => ({
              ...item,
              id: index + 1,
            })
          );
          setReportGridData([...updatedReportGridData]);
          setIsQueryRun(true);
        },
        onError: (error) => {
          setIsQueryRun(false);
          setToastMsgConfig({
            show: true,
            type: ToastType.Error,
            message:
              error?.message ??
              t('reports.reviewerProductivity.reportAPIError'),
          });
        },
      });
    },
    [fetchReviewerProductivityView]
  );

  const exportReport = useCallback(() => {
    const {selectedAssignments, fromDateTime, toDateTime} = savedFilterValues;
    const payload = {
      selectionCriteria: {
        selectedAssignments,
        selectedReviewers: getSelectedReviewer(
          savedFilterValues,
          leftPanelDetails
        ),
        fromDateTime,
        toDateTime,
        reportTypeCode: ReportTypeCode.REVIEWER_PRODUCTIVITY_REPORT,
        assignmentFlag: selectedAssignments?.length > 0,
        selectedCustomColumns: hiddenColumnsRef.current,
        dataAsOf: dayjs
          .utc(dataAsOf, 'YYYY/MM/DD HH:mm:ss', true)
          .toISOString(),
      },
      actionType: REPORT_ACTION_TYPE,
      reportType: ReportTypeCode.REVIEWER_PRODUCTIVITY_REPORT,
    };
    setColumnVisibility();
    exportReviewerCounts(payload as any, {
      onSuccess: () => {
        setToastMsgConfig({
          show: true,
          type: ToastType.Success,
          message: t('reports.common.reportExportSuccess'),
        });
      },
      onError: (error) => {
        setToastMsgConfig({
          show: true,
          type: ToastType.Error,
          message: error?.message ?? t('reports.common.reportExportError'),
        });
      },
    });
  }, [
    exportReviewerCounts,
    leftPanelDetails,
    savedFilterValues,
    setColumnVisibility,
    dataAsOf,
  ]);

  const onCompleteWaitModal = useCallback(
    (_: number) => {
      const {reportId, reportName, reportFilePath, assignmentFlag} =
        productivityViewPayload!;

      setProductivityViewPayload({
        ...productivityViewPayload!,
        notificationId: undefined,
      });

      reviewerProductivityViewRequest({
        reportId,
        reportName,
        reportFilePath,
        assignmentFlag,
      });
    },
    [productivityViewPayload, reviewerProductivityViewRequest]
  );

  const onCancelWaitModal = useCallback(
    (notificationFlag: boolean) => {
      if (notificationFlag) {
        setProductivityViewPayload({
          ...productivityViewPayload!,
          notificationId: undefined,
        });
        resetReviewerProductivityView();
        resetReviewerProductivityReport();
      }
    },
    [
      productivityViewPayload,
      resetReviewerProductivityReport,
      resetReviewerProductivityView,
    ]
  );

  const rightPanelHeaderConfig = useMemo(
    () => ({
      subTitle: t('reports.reviewerProductivity.dateAsOf', {
        date: dataAsOf,
      }),
      showUploadIcon: true,
      showSaveIcon: true,
      showColumnsIcon: true,
      showActionButton: false,
      onUploadClick: exportReport,
    }),
    [exportReport, dataAsOf]
  );

  const loadReportConfig = useCallback(async () => {
    try {
      const data = await import(`../../config/${REPORT_CONFIG_FILE_NAME}.json`);
      defaultReportConfig.current = {...data.default};
      setReportConfig(data.default);
    } catch (error) {
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message: t('reports.reviewerProductivity.reportConfigAPIError'),
      });
      console.error('Error loading JSON:', error);
    }
  }, []);

  const updatedReportConfig = useMemo(
    () =>
      leftPanelDetails
        ? mergeApiDataIntoConfig(
            reportConfig!,
            leftPanelDetails,
            savedFilterValues
          )
        : reportConfig,
    [leftPanelDetails, reportConfig, savedFilterValues]
  );

  const saveReportConfigPayload = useMemo(() => {
    if (productivityViewPayload?.reportId && savedFilterValues) {
      const {selectedAssignments, fromDateTime, toDateTime} = savedFilterValues;
      return {
        reportId: productivityViewPayload?.reportId,
        selectedAssignments,
        selectedReviewers: getSelectedReviewer(
          savedFilterValues,
          leftPanelDetails ?? {}
        ),
        fromDateTime,
        toDateTime,
        reportTypeCode: ReportTypeCode.REVIEWER_PRODUCTIVITY_REPORT,
        assignmentFlag: true,
        selectedCustomColumns: hiddenColumnsRef.current,
      };
    }
    return undefined;
  }, [
    productivityViewPayload?.reportId,
    savedFilterValues,
    leftPanelDetails,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(hiddenColumnsRef.current),
  ]);

  const handleActionClick = useCallback(
    async (actionKey: string, values: Record<string, any>) => {
      switch (actionKey) {
        case 'applyChanges': {
          setDataAsOf(dayjs.utc().format('YYYY/MM/DD HH:mm:ss'));
          const filterValues = {
            ...values,
            selectedReviewers: getSelectedReviewer(values, leftPanelDetails),
          };
          setCurrentPage(INIT_PAGE_NUMBER);
          setPageSize(INIT_PAGE_SIZE);
          setReportGridData([]);
          setSavedFilterValues(values);
          reviewerProductivityReportRequest(filterValues);
          break;
        }
        case 'reset': {
          resetLeftPanelReport.current = true;
          setIsQueryRun(false);
          setReportConfig({...defaultReportConfig.current});
          setCurrentPage(INIT_PAGE_NUMBER);
          setPageSize(INIT_PAGE_SIZE);
          setReportGridData([]);
          setSavedFilterValues({});
          resetReviewerProductivityView();
          resetReviewerProductivityReport();
          break;
        }
      }
    },
    [
      leftPanelDetails,
      resetReviewerProductivityReport,
      resetReviewerProductivityView,
      reviewerProductivityReportRequest,
    ]
  );

  const onPageChange = useCallback(
    (pageNumber: number, _pageSize?: number) => {
      if (pageNumber !== currentPage) {
        setCurrentPage(pageNumber);
      }
      if (_pageSize && _pageSize !== pageSize) {
        setPageSize(_pageSize ?? INIT_PAGE_SIZE);
      }
    },
    [currentPage, pageSize]
  );

  const handleColumnToggleAction = useCallback(async (columnId: string) => {
    const columnIndex = reviewerProductivityColumnConfig.findIndex(
      (colConfig: ColumnConfig) => colConfig.field === columnId
    );
    if (hiddenColumnsRef.current.includes(columnIndex)) {
      const index = hiddenColumnsRef.current.indexOf(columnIndex);
      index >= 0 && hiddenColumnsRef.current.splice(index, 1);
    } else {
      hiddenColumnsRef.current.push(columnIndex);
    }
    hiddenColumnsRef.current = sortBy(hiddenColumnsRef.current);
  }, []);

  const rowCount = useMemo(
    () => reportGridData?.length ?? 0,
    [reportGridData?.length]
  );

  const gridConfig = useMemo(
    (): ReportGridProps => ({
      className: clsx('reviewer-productivity-grid'),
      primaryKey: ROW_PRIMARY_KEY,
      totalRecords: rowCount,
      disableVirtualization: false,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      itemsPerPage: pageSize,
      currentPage,
      paginationMode: 'client',
      sortingMode: 'client',
      getRowHeight: () => 'auto',
      hideFooter: rowCount <= PAGE_SIZE_OPTIONS[0],
      onPageChange,
    }),
    [currentPage, onPageChange, pageSize, rowCount]
  );

  useEffect(() => {
    if (leftPanelReport && leftPanelDetails) {
      const {reviewerList = []} = leftPanelDetails;
      const {
        selectedReviewers = [],
        selectedAssignments = [],
        fromDateTime,
        toDateTime,
      } = leftPanelReport;

      const getSelectedFilters = {
        selectedReviewers: reviewerList?.map((item: any) =>
          selectedReviewers?.includes(item?.reviewerID)
        ),
        selectedAssignments: selectedAssignments?.map((value) =>
          value?.toString()
        ),
        fromDateTime,
        toDateTime,
      };

      setSavedFilterValues(getSelectedFilters);
    }
  }, [leftPanelDetails, leftPanelReport]);

  useEffect(() => {
    loadReportConfig();
  }, [loadReportConfig]);

  useEffect(() => {
    if (savedReportStateData) {
      const {reviewerProductivityReportDetails = []} =
        savedReportStateData ?? {};
      const updatedReportGridData = (
        reviewerProductivityReportDetails ?? []
      ).map((item: Record<string, any>, index: number) => ({
        ...item,
        id: index + 1,
      }));
      setReportGridData([...updatedReportGridData]);
    }
  }, [savedReportStateData]);

  useEffect(() => {
    if (reportStateError) {
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message:
          (reportStateError as any)?.response?.data?.title ??
          t('reports.common.reportAPIError'),
      });
    }
  }, [reportStateError]);

  return isEmpty(reportConfig) ||
    isEmpty(leftPanelDetails) ||
    isEmpty(updatedReportConfig) ||
    (!resetLeftPanelReport.current &&
      isEmpty(savedFilterValues) &&
      urlReportId) ||
    (urlReportId && isEmpty(leftPanelReport)) ||
    isReportStateLoading ? (
    <ArrLoadingSpinner isOpen overlay />
  ) : (
    <Fragment>
      <StandardReports
        className={'reviewer-productivity-container'}
        title={
          urlReportId && reportLabel ? reportLabel : (reportConfig?.title ?? '')
        }
        infoContent={reportConfig?.infoContent}
        infoModalTitle={reportConfig?.infoModalTitle ?? ''}
        showInfoIcon={reportConfig?.showInfoIcon ?? false}
        reportsConfig={updatedReportConfig?.config}
        selectedOption={null}
        selectedFilterValues={savedFilterValues}
        data={reportGridData}
        loading={isProductivityReportLoading || isProductivityReviewLoading}
        columns={columnConfig}
        mapRows={(data) => data}
        enableSave={isQueryRun}
        getRowId={(row) => row?.[ROW_PRIMARY_KEY ?? '']}
        rightPanelHeaderConfig={rightPanelHeaderConfig}
        onAction={handleActionClick}
        gridConfig={gridConfig}
        saveReportConfig={saveReportConfigPayload}
        onColumnToggleAction={handleColumnToggleAction}
      />
      {productivityViewPayload?.reportId &&
      productivityViewPayload?.notificationId ? (
        <ArrWaitToCompleteModalWrapper
          showClose={false}
          reportId={productivityViewPayload?.reportId}
          notificationId={productivityViewPayload?.notificationId}
          onComplete={onCompleteWaitModal}
          onFlag={onCancelWaitModal}
        />
      ) : null}
      {isExportReviewerCountsLoading ? (
        <ArrLoadingSpinner isOpen overlay />
      ) : null}
      {toastMsgConfig.show ? (
        <ArrToastNotification
          onClose={() => setToastMsgConfig({show: false})}
          status={[toastMsgConfig.type ?? ToastType.Success]}
          message={toastMsgConfig.message ?? ''}
          title={toastMsgConfig.title}
        />
      ) : null}
    </Fragment>
  );
};

export default ReviewerProductivityContainer;
